import React from 'react';
import {
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import CurrencySelect from '@components/shared/selects/CurrencySelect';
import type { CurrencyOption } from './types';

const { TextArea } = Input;

interface OrderOptionsCardProps {
  showCod: boolean;
  showDeliveryWithPickup: boolean;
  currencies?: CurrencyOption[];
  /** When true, disable past dates on the date pickers (used by new-order flow). */
  disablePastDates?: boolean;
  /** When true, render the ⓘ tooltip on `scheduled_shipping_date` (edit flow). */
  showScheduledShippingDateTooltip?: boolean;
}

const OrderOptionsCard: React.FC<OrderOptionsCardProps> = ({
  showCod,
  showDeliveryWithPickup,
  currencies,
  disablePastDates = false,
  showScheduledShippingDateTooltip = false,
}) => {
  const { t } = useTranslation();

  const disabledDate = disablePastDates
    ? (current: dayjs.Dayjs) => current && current.isBefore(dayjs(), 'day')
    : undefined;

  const scheduledLabel = showScheduledShippingDateTooltip ? (
    <span>
      {t('models.order.scheduled_shipping_date')}{' '}
      <Tooltip title={t('shared.orders.form.scheduled_shipping_date_tooltip')}>
        <span style={{ color: '#999', cursor: 'help' }}>ⓘ</span>
      </Tooltip>
    </span>
  ) : (
    t('models.order.scheduled_shipping_date')
  );

  return (
    <Card
      title={t('shared.orders.form.sections.order_options')}
      style={{ marginBottom: 24, position: 'sticky', top: 24 }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Form.Item name="is_cod" valuePropName="checked" noStyle>
          <Checkbox disabled={!showCod}>{t('models.order.is_cod')}</Checkbox>
        </Form.Item>
        <Form.Item name="delivery_with_pickup" valuePropName="checked" noStyle>
          <Checkbox disabled={!showDeliveryWithPickup}>{t('models.order.delivery_with_pickup')}</Checkbox>
        </Form.Item>
        <Form.Item name="urgent" valuePropName="checked" noStyle>
          <Checkbox>{t('models.order.urgent')}</Checkbox>
        </Form.Item>
      </Space>

      <Divider />

      <Form.Item name="convenience_store_code" label={t('models.order.convenience_store_code')}>
        <Input placeholder={t('models.order.convenience_store_code') as string} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="total_amount"
            label={t('models.order.total_amount')}
            rules={[{ required: true, message: t('common.validation.required') as string }]}
          >
            <Input type="number" placeholder="0.00" prefix="$" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="currency"
            label={t('models.order.currency')}
            rules={[{ required: true, message: t('common.validation.required') as string }]}
          >
            <CurrencySelect currencies={currencies} />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Form.Item name="scheduled_shipping_date" label={scheduledLabel}>
        <DatePicker
          style={{ width: '100%' }}
          placeholder={t('common.placeholder.select_date') as string}
          disabledDate={disabledDate}
        />
      </Form.Item>

      <Form.Item name="delivery_date" label={t('models.order.delivery_date')}>
        <DatePicker
          style={{ width: '100%' }}
          placeholder={t('common.placeholder.select_date') as string}
          disabledDate={disabledDate}
        />
      </Form.Item>

      <Form.Item name="delivery_time" label={t('models.order.delivery_time')}>
        <Radio.Group>
          <Radio.Button value="AM">{t('enums.order.delivery_time.am')}</Radio.Button>
          <Radio.Button value="PM">{t('enums.order.delivery_time.pm')}</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Divider />

      <Form.Item name="note" label={t('models.order.note')}>
        <TextArea rows={3} placeholder={t('models.order.note') as string} />
      </Form.Item>

      <Collapse ghost size="small">
        <Collapse.Panel header={t('shared.orders.form.sections.advanced_info') as string} key="advanced">
          <Form.Item name="seller_name" label={t('models.order.seller_name')}>
            <Input placeholder={t('models.order.seller_name') as string} />
          </Form.Item>

          <Form.Item name="seller_phone" label={t('models.order.seller_phone')}>
            <Input placeholder={t('models.order.seller_phone') as string} />
          </Form.Item>

          <Form.Item name="invoice_number" label={t('models.order.invoice_number')}>
            <Input placeholder={t('models.order.invoice_number') as string} />
          </Form.Item>
        </Collapse.Panel>
      </Collapse>
    </Card>
  );
};

export default OrderOptionsCard;
